import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Car, Clock, Phone, MapPin, ArrowLeft, Mail, User, Loader2, Camera } from 'lucide-react';
// Bundle Tesseract worker/core via Vite as same-origin asset URLs
// @ts-ignore - Vite's ?url returns a string
import { supabase } from '../lib/supabase';
import { format, addHours, addMonths, addYears, startOfDay, startOfMonth, isSameDay } from 'date-fns';
import toast from 'react-hot-toast';
import Footer from './Footer';
import Logo from './marketing/Logo';
import { useLot } from '../contexts/LotContext';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';

function ParkingForm() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { lot, settings, userRole, isLoading, error } = useLot();
  
  const [licensePlate, setLicensePlate] = useState('');
  const [hours, setHours] = useState(1);
  const [phone, setPhone] = useState('');
  const [isMonthly, setIsMonthly] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [capacityData, setCapacityData] = useState<Record<string, { count: number; max: number | null; isFull: boolean }>>({});
  const [displayedMonth, setDisplayedMonth] = useState<Date>(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  const expiryTime = isMonthly 
    ? addMonths(settings?.scheduled_subscriptions_enabled ? startDate : new Date(), 1)
    : addHours(new Date(), hours > (settings?.max_hours || 12) / 4 ? settings?.max_hours || 12 : hours);

  useEffect(() => {
    // Context handles lot data loading, no need for separate effects
  }, []);

  // Initialize tab selection from URL parameters
  useEffect(() => {
    const monthlyParam = searchParams.get('monthly');
    const tabParam = searchParams.get('tab');

    const monthlyRequested = (
      (monthlyParam !== null && monthlyParam.toLowerCase() !== 'false' && monthlyParam !== '0') ||
      (tabParam !== null && tabParam.toLowerCase() === 'monthly')
    );

    if (monthlyRequested) {
      setIsMonthly(true);
    }
  }, [searchParams]);

  const updateTabInUrl = (monthly: boolean) => {
    const next = new URLSearchParams(searchParams);
    next.delete('monthly'); // prefer canonical tab param
    next.set('tab', monthly ? 'monthly' : 'once');
    setSearchParams(next, { replace: true });
  };

  // Fetch subscription capacity for the displayed month (always check when monthly tab is active)
  useEffect(() => {
    if (!isMonthly || !lot) return;
    const monthKey = format(displayedMonth, 'yyyy-MM');
    if (capacityData[monthKey] !== undefined) return;

    const fetchCapacity = async () => {
      try {
        const apiUrl = window.location.hostname === 'localhost'
          ? `/api/subscription-capacity?lot_id=${lot.id}&month=${monthKey}`
          : `/.netlify/functions/subscription-capacity?lot_id=${lot.id}&month=${monthKey}`;
        const response = await fetch(apiUrl);
        if (response.ok) {
          const data = await response.json();
          setCapacityData(prev => ({ ...prev, [monthKey]: data }));
        }
      } catch (err) {
        console.error('Error fetching subscription capacity:', err);
      }
    };
    fetchCapacity();
  }, [displayedMonth, isMonthly, lot]);

  // Auto-advance startDate to the first available month when the selected month is full.
  // Works iteratively: if the next month is also full, the startDate change re-triggers
  // this effect and we keep looking forward until an open month is found.
  useEffect(() => {
    if (!isMonthly || !settings?.scheduled_subscriptions_enabled) return;

    const monthKey = format(startDate, 'yyyy-MM');
    const capacity = capacityData[monthKey];
    if (!capacity?.isFull) return;

    const nextMonthStart = startOfMonth(addMonths(startDate, 1));
    const nextMonthKey = format(nextMonthStart, 'yyyy-MM');

    if (capacityData[nextMonthKey] === undefined) {
      // Capacity not loaded yet — scroll calendar there to trigger the fetch effect
      setDisplayedMonth(nextMonthStart);
      return;
    }

    if (!capacityData[nextMonthKey].isFull) {
      // Next month has space — jump to its first day (min 48h from now per Stripe rule)
      const minDate = new Date(Date.now() + 48 * 60 * 60 * 1000);
      const target = nextMonthStart < minDate ? startOfDay(minDate) : nextMonthStart;
      setStartDate(target);
      setDisplayedMonth(nextMonthStart);
      toast(`${format(nextMonthStart, 'MMMM')} is the next available month`, { icon: '📅' });
    }
    // If next month is also full, the startDate update re-triggers this effect for that month
  }, [capacityData, startDate, isMonthly, settings?.scheduled_subscriptions_enabled]);

  // Show loading state if lot data is still loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-confetti-pink mx-auto mb-4" />
          <p className="text-ink-500">Loading parking lot...</p>
        </div>
      </div>
    );
  }

  // Show error state if lot data failed to load
  if (error || !lot || !settings) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <MapPin className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-ink-900 mb-2">
            Unable to Load Parking Lot
          </h2>
          <p className="text-ink-500 mb-4">
            {error || 'Lot information not available'}
          </p>
          <Link
            to="/"
            className="inline-flex items-center px-4 py-2 bg-confetti-pink text-white rounded-lg hover:bg-confetti-pink/90 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isMonthly && (hours <= 0 || hours < 1)) {
      toast.error('Please enter a valid number of hours');
      return;
    }

    if (isMonthly && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!isValidNorthAmericanPhone(phone)) {
      setPhoneError('Please enter a valid 10-digit North American phone number.');
      return;
    }

    const amount = isMonthly ? (settings.subscription_amount || 150) : (hours <= 3 ? hours * settings.hourly_rate : settings.max_daily_rate);

    if (isMonthly) {
      const targetDate = isScheduled ? startDate : new Date();
      const monthKey = format(targetDate, 'yyyy-MM');
      const capacity = capacityData[monthKey];
      if (capacity?.isFull) {
        toast.error('This lot is currently full for the selected month. Please try a different month or check back later.');
        return;
      }
    }

    if (isScheduled && startDate.getTime() < Date.now() + 48 * 60 * 60 * 1000) {
      toast.error('Scheduled start date must be at least 2 days in the future. Select today to start immediately.');
      return;
    }
    const sessionData = {
      license_plate: licensePlate.toUpperCase(),
      phone,
      hours: isMonthly ? 720 : hours,
      amount,
      expires_at: expiryTime.toISOString(),
      location: lot.display_name, // Keep for backward compatibility
      lot_id: lot.id, // New lot-specific reference
      first_name: isMonthly ? firstName : null,
      last_name: isMonthly ? lastName : null,
      email: isMonthly ? email : null,
      auto_renew: isMonthly,
      payment_method_type: isMonthly ? 'subscription' : null, // Pre-label subscriptions for proper categorization
      session_type: isMonthly ? 'Monthly' : 'New',
      scheduled_start_date: isScheduled ? startDate.toISOString() : null,
    };

    console.log('Creating parking session with data:', sessionData);
    console.log('Lot info:', { id: lot.id, slug: lot.slug, display_name: lot.display_name });

    const { data, error } = await supabase
      .from('parking_sessions')
      .insert([sessionData])
      .select()
      .single();

    if (error) {
      console.error('Parking session creation error:', error);
      toast.error(`Failed to create parking session: ${error.message || 'Unknown error'}`);
      return;
    }

    navigate(`/lots/${lot.slug}/payment/${data.id}`);
  };

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Allow empty input temporarily so users can delete the entire value
    if (inputValue === '') {
      setHours(0);
      return;
    }
    
    const value = parseInt(inputValue);
    
    if (isNaN(value)) {
      setHours(1);
    } else if (value > settings.max_hours) {
      setHours(settings.max_hours);
      toast.error(`Maximum parking duration is ${settings.max_hours} hours`);
    } else {
      setHours(value);
    }
  };

  const isValidNorthAmericanPhone = (value: string): boolean => {
    const digits = value.replace(/\D/g, '');
    // Accept 11 digits with leading country code 1, or plain 10 digits
    const core = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    // Must be exactly 10 digits and area code must not start with 0 or 1
    return core.length === 10 && /^[2-9]/.test(core);
  };

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return cleaned;
  };

  const calculateAmount = () => {
    if (isMonthly) return settings.subscription_amount || 150;
    return hours <= 3 ? hours * settings.hourly_rate : settings.max_daily_rate;
  };

  const openCamera = () => {
    cameraInputRef.current?.click();
  };

  const resizeImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          // Slightly larger and sharpen for mobile captures
          const maxDim = 1600;
          let { width, height } = img;
          if (width > height && width > maxDim) {
            height = (maxDim / width) * height;
            width = maxDim;
          } else if (height > maxDim) {
            width = (maxDim / height) * width;
            height = maxDim;
          }
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(width);
          canvas.height = Math.round(height);
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas not supported'));
            return;
          }
          // Apply a light sharpening kernel to help OCR on mobile
          try {
            // Draw first
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            // Optional: attempt to increase contrast
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            for (let i = 0; i < data.length; i += 4) {
              // simple contrast/brightness tweak
              let r = data[i];
              let g = data[i + 1];
              let b = data[i + 2];
              r = Math.min(255, Math.max(0, (r - 128) * 1.1 + 128));
              g = Math.min(255, Math.max(0, (g - 128) * 1.1 + 128));
              b = Math.min(255, Math.max(0, (b - 128) * 1.1 + 128));
              data[i] = r;
              data[i + 1] = g;
              data[i + 2] = b;
            }
            ctx.putImageData(imageData, 0, 0);
          } catch {}
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = reader.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const extractPlateFromText = (rawText: string) => {
    const upper = rawText.toUpperCase();
    const stopWords = [
      // Canadian provinces/territories (with and without spaces)
      'ONTARIO','QUEBEC','ALBERTA','MANITOBA','SASKATCHEWAN','NOVA SCOTIA','NEW BRUNSWICK',
      'NEWFOUNDLAND','LABRADOR','PRINCE EDWARD ISLAND','PRINCEEDWARDISLAND','PEI','YUKON',
      'NUNAVUT','NORTHWEST TERRITORIES','NORTHWESTTERRITORIES','BRITISH COLUMBIA','BRITISHCOLUMBIA',
      // Common US examples
      'CALIFORNIA','TEXAS','FLORIDA','NEW YORK','NEWYORK','PENNSYLVANIA','OHIO','GEORGIA'
    ];
    let cleaned = upper.replace(/[\n\r]/g, ' ');
    // Remove known jurisdiction words regardless of spacing
    for (const word of stopWords) {
      const pattern = new RegExp(word.replace(/\s+/g, ''), 'g');
      cleaned = cleaned.replace(pattern, '');
      cleaned = cleaned.replace(new RegExp(word, 'g'), '');
    }
    // Remove common separators
    cleaned = cleaned.replace(/[-:_]/g, ' ');
    // Collect candidate tokens 2-8 chars
    const tokens = (cleaned.match(/[A-Z0-9]{2,8}/g) || []).filter(t => t.length >= 2 && t.length <= 8);
    if (tokens.length === 0) {
      const fallback = upper.replace(/[^A-Z0-9]/g, '');
      return fallback.slice(0, 8);
    }
    // Score tokens: prefer mixed alnum and length 5-7, choose the last best (plates often at bottom)
    const score = (t: string) => {
      const len = t.length;
      const hasLetter = /[A-Z]/.test(t) ? 1 : 0;
      const hasDigit = /[0-9]/.test(t) ? 1 : 0;
      const mixed = hasLetter && hasDigit ? 2 : 0; // prefer mixed
      const lenIdeal = Math.max(0, 3 - Math.abs(6 - len)); // peak at 6
      return mixed * 3 + lenIdeal;
    };
    let best = tokens[0];
    let bestScore = score(best);
    for (const t of tokens) {
      const s = score(t) + (cleaned.lastIndexOf(t) / 1000); // slight bias to later tokens
      if (s >= bestScore) {
        best = t;
        bestScore = s;
      }
    }
    return best.toUpperCase();
  };

  const handleCameraFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsScanning(true);
    try {
      const dataUrl = await resizeImage(file);
      // Prefer server OCR to avoid client CSP/worker issues
      const ocrResponse = await fetch('/api/ocr-plate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageDataUrl: dataUrl })
      });
      if (!ocrResponse.ok) throw new Error('OCR request failed');
      const ocrJson = await ocrResponse.json();
      const plate = extractPlateFromText(ocrJson.text || '');
      if (plate && plate.length >= 2) {
        setLicensePlate(plate);
        toast.success(`Detected plate: ${plate}`);
      } else {
        toast.error('Could not read a plate from the photo');
      }
    } catch (err) {
      console.error('OCR error', err);
      toast.error('Camera or OCR failed. Please enter the plate manually.');
    } finally {
      setIsScanning(false);
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const isScheduled = isMonthly && !!settings?.scheduled_subscriptions_enabled && !isSameDay(startDate, new Date());

  const brandColor = settings.brand_color || null;
  const logoUrl = settings.logo_url || null;
  const monthlyEnabled = settings.monthly_enabled !== false;
  const accentStyle = brandColor ? { backgroundColor: brandColor } : undefined;
  const focusClass = brandColor ? '' : 'focus:ring-confetti-pink';

  return (
    <div className="min-h-screen bg-cream-100 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6 space-y-6">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-900 mb-4">
            <ArrowLeft size={20} />
            Choose a different location
          </Link>
          <div className="flex items-center justify-center mb-2">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={`${lot.display_name} logo`}
                className="h-10 w-auto max-w-[180px] object-contain"
              />
            ) : (
              <Logo size={28} />
            )}
          </div>
          <div className="flex items-center justify-center gap-2 mt-2 text-ink-500">
            <MapPin size={20} />
            <p>{lot.display_name}</p>
            {lot.address && (
              <span className="text-sm text-gray-500">• {lot.address}</span>
            )}
          </div>
        </div>

        {monthlyEnabled && (
          <div className="flex justify-center gap-4 p-2 bg-cream-200 rounded-lg">
            <button
              onClick={() => { setIsMonthly(false); updateTabInUrl(false); }}
              className={`px-4 py-2 rounded-md transition-colors ${
                !isMonthly
                  ? 'text-white'
                  : 'bg-transparent text-ink-500 hover:bg-cream-300'
              }`}
              style={!isMonthly ? accentStyle : undefined}
            >
              Pay Once
            </button>
            <button
              onClick={() => { setIsMonthly(true); updateTabInUrl(true); }}
              className={`px-4 py-2 rounded-md transition-colors ${
                isMonthly
                  ? 'text-white'
                  : 'bg-transparent text-ink-500 hover:bg-cream-300'
              }`}
              style={isMonthly ? accentStyle : undefined}
            >
              Pay Monthly
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink-700">License Plate</label>
            <div className="mt-1 relative">
              <Car
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400"
                size={20}
              />
              <input
                type="text"
                required
                value={licensePlate}
                onChange={(e) => setLicensePlate(e.target.value)}
                className="pl-10 pr-12 w-full rounded-lg border border-ink-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-confetti-pink"
                placeholder="Enter license plate"
              />
              <button
                type="button"
                onClick={openCamera}
                className="md:hidden absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                aria-label="Scan license plate with camera"
              >
                {isScanning ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Camera className="h-5 w-5" />
                )}
              </button>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleCameraFile}
                className="hidden"
                aria-hidden="true"
              />
            </div>
          </div>

          {isMonthly && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink-700">First Name</label>
                  <div className="mt-1 relative">
                    <User
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400"
                      size={20}
                    />
                    <input
                      type="text"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="pl-10 w-full rounded-lg border border-ink-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-confetti-pink"
                      placeholder="First name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-700">Last Name</label>
                  <div className="mt-1 relative">
                    <User
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400"
                      size={20}
                    />
                    <input
                      type="text"
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="pl-10 w-full rounded-lg border border-ink-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-confetti-pink"
                      placeholder="Last name"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700">Email Address</label>
                <div className="mt-1 relative">
                  <Mail
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400"
                    size={20}
                  />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 w-full rounded-lg border border-ink-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-confetti-pink"
                    placeholder="email@example.com"
                  />
                </div>
              </div>
            </>
          )}

          {!isMonthly && (
            <div>
              <label className="block text-sm font-medium text-ink-700">
                Hours (max {settings.max_hours})
              </label>
              <div className="mt-1 relative">
                <Clock
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400"
                  size={20}
                />
                <input
                  type="number"
                  required
                  min="1"
                  max={settings.max_hours}
                  value={hours || ''}
                  onChange={handleHoursChange}
                  className="pl-10 w-full rounded-lg border border-ink-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-confetti-pink"
                />
              </div>
              <p className="mt-1 text-sm text-ink-500">
                {hours <= 3
                  ? `$${settings.hourly_rate.toFixed(2)}/hour for up to 3 hours`
                  : `Maximum daily rate of $${settings.max_daily_rate.toFixed(2)} applied`}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-ink-700">
              Phone Number
            </label>
            <div className="mt-1 relative">
              <Phone
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${phoneError ? 'text-red-400' : 'text-ink-400'}`}
                size={20}
              />
              <input
                type="tel"
                required
                value={phone}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value);
                  setPhone(formatted);
                  if (phoneError) setPhoneError('');
                }}
                onBlur={() => {
                  if (phone && !isValidNorthAmericanPhone(phone)) {
                    setPhoneError('Please enter a valid 10-digit North American phone number.');
                  }
                }}
                maxLength={14}
                className={`pl-10 w-full rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 ${
                  phoneError
                    ? 'border-red-400 focus:ring-red-400'
                    : 'border-ink-300 focus:ring-confetti-pink'
                }`}
                placeholder="(555) 555-5555"
              />
            </div>
            {phoneError && (
              <p className="mt-1 text-sm text-red-600">{phoneError}</p>
            )}
          </div>

          {isMonthly && settings?.scheduled_subscriptions_enabled && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-ink-700">
                Subscription Start Date
              </label>
              <button
                type="button"
                onClick={() => setCalendarOpen(!calendarOpen)}
                className="w-full flex items-center justify-between rounded-lg border border-ink-300 px-4 py-2 text-left focus:outline-none focus:ring-2 focus:ring-confetti-pink hover:bg-cream-100 transition-colors"
              >
                <span className={isSameDay(startDate, new Date()) ? 'text-ink-700' : 'text-confetti-pink font-medium'}>
                  {isSameDay(startDate, new Date()) ? 'Today' : format(startDate, 'MMMM d, yyyy')}
                </span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${calendarOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {calendarOpen && (
                <div className="border border-ink-300 rounded-lg p-3 flex flex-col items-center">
                  <DayPicker
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      if (date) {
                        setStartDate(date);
                        setCalendarOpen(false);
                      }
                    }}
                    onMonthChange={setDisplayedMonth}
                    disabled={(date) => {
                      if (date < startOfDay(new Date())) return true;
                      if (date > addYears(new Date(), 1)) return true;
                      // Stripe requires trial_end >= 48 hours in the future
                      if (!isSameDay(date, new Date()) && date.getTime() < Date.now() + 48 * 60 * 60 * 1000) return true;
                      const monthKey = format(date, 'yyyy-MM');
                      return capacityData[monthKey]?.isFull === true;
                    }}
                    month={displayedMonth}
                  />
                  <p className="text-xs text-ink-500 mt-1 text-center">
                    Scheduled dates must be at least 2 days in the future
                  </p>
                  {(() => {
                    const monthKey = format(displayedMonth, 'yyyy-MM');
                    const capacity = capacityData[monthKey];
                    if (capacity?.isFull) {
                      return (
                        <p className="text-sm text-red-600 font-medium mt-2">
                          Month Full — no more subscriptions available
                        </p>
                      );
                    }
                    if (capacity && capacity.max !== null) {
                      return (
                        <p className="text-xs text-gray-500 mt-2">
                          {capacity.count} / {capacity.max} subscriptions this month
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
              {!isSameDay(startDate, new Date()) && (
                <p className="text-sm text-confetti-pink">
                  Subscription scheduled to start on {format(startDate, 'MMMM d, yyyy')}
                </p>
              )}
            </div>
          )}

          <div className="bg-cream-100 p-4 rounded-lg space-y-2">
            <div className="flex flex-col gap-1">
              <p className="text-lg font-semibold text-ink-900">
                Total: ${calculateAmount().toFixed(2)}
                {isMonthly && <span className="text-sm text-ink-500"> /month</span>}
              </p>
              <p className="text-lg font-semibold text-confetti-pink">
                {isMonthly ? 'Renews' : 'Expires'}: {format(expiryTime, 'PPp')}
              </p>
              {isMonthly && (
                <p className="text-sm text-ink-500">
                  Auto-renews monthly. Cancel anytime.
                </p>
              )}
            </div>
          </div>

          {isMonthly && capacityData[format(isScheduled ? startDate : new Date(), 'yyyy-MM')]?.isFull && (
            <p className="text-sm text-red-600 font-medium text-center bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              This lot is currently full — no more subscriptions available for this month.
            </p>
          )}

          <button
            type="submit"
            disabled={isMonthly && capacityData[format(isScheduled ? startDate : new Date(), 'yyyy-MM')]?.isFull === true}
            className="w-full text-white py-2 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            style={accentStyle ?? { backgroundColor: '#EC4899' }}
          >
            Continue to Payment
          </button>
        </form>

        <Footer hasCustomLogo={!!logoUrl} />
      </div>
    </div>
  );
}

export default ParkingForm;